import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useCreateCity, useUpdateCity, useDeleteCity, useCities } from "@/hooks/useCities";
import { useCountries } from "@/hooks/useCountries";
import { City } from "@/types/city";
import { toast } from "sonner";
interface CityDialogProps {
  city?: City;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
export const CityDialog = ({
  city,
  trigger,
  onSuccess,
  open: controlledOpen,
  onOpenChange
}: CityDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    country_id: ""
  });
  
  const { data: countries = [] } = useCountries();
  const { data: cities = [] } = useCities();
  const createCity = useCreateCity();
  const updateCity = useUpdateCity();
  const deleteCity = useDeleteCity();

  // تحديث البيانات عند تغيير city prop
  useEffect(() => {
    if (city) {
      setFormData({
        name_ar: city.name_ar || "",
        name_en: city.name_en || "",
        country_id: city.country_id || ""
      });
    }
  }, [city]);

  // تفريغ البيانات عند فتح الحوار للإضافة
  useEffect(() => {
    if (open && !city) {
      setFormData({
        name_ar: "",
        name_en: "",
        country_id: ""
      });
    }
  }, [open, city]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من عدم التكرار
    const existingCity = cities.find(c => 
      c.name_ar.toLowerCase() === formData.name_ar.toLowerCase() && 
      c.country_id === formData.country_id &&
      (!city || c.id !== city.id)
    );
    
    if (existingCity) {
      toast.error("هذه المدينة موجودة بالفعل في نفس الدولة");
      return;
    }

    try {
      if (city) {
        await updateCity.mutateAsync({
          id: city.id,
          ...formData
        });
        toast.success("تم تحديث المدينة بنجاح");
      } else {
        await createCity.mutateAsync(formData);
        toast.success("تم إضافة المدينة بنجاح");
      }
      setOpen(false);
      onSuccess?.();
      if (!city) {
        setFormData({
          name_ar: "",
          name_en: "",
          country_id: ""
        });
      }
    } catch (error) {
      toast.error("حدث خطأ أثناء حفظ البيانات");
    }
  };
  const handleDelete = async () => {
    if (!city) return;
    try {
      await deleteCity.mutateAsync(city.id);
      toast.success("تم حذف المدينة بنجاح");
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      toast.error("حدث خطأ أثناء حذف المدينة");
    }
  };
  return <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{city ? "تعديل المدينة" : "إضافة مدينة جديدة"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name_ar">الاسم بالعربية *</Label>
            <Input id="name_ar" value={formData.name_ar} onChange={e => setFormData({
            ...formData,
            name_ar: e.target.value
          })} required />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
            <Input id="name_en" value={formData.name_en} onChange={e => setFormData({
            ...formData,
            name_en: e.target.value
          })} />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="country_id">الدولة *</Label>
            <Select value={formData.country_id} onValueChange={value => setFormData({
            ...formData,
            country_id: value
          })} required>
              <SelectTrigger>
                <SelectValue placeholder="اختر الدولة" />
              </SelectTrigger>
              <SelectContent>
                {countries.map(country => <SelectItem key={country.id} value={country.id}>
                    {country.name_ar}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex justify-between gap-2">
            {city && <Button type="button" variant="destructive" onClick={handleDelete} className="gap-2">
                <Trash2 className="w-4 h-4" />
                حذف
              </Button>}
            <div className="flex gap-2 mr-auto">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit">
                {city ? "تحديث" : "إضافة"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>;
};